// Singleton wiring: MIDI + simulator -> pipeline -> signal engine. Exposes window.__spore.
import { createSignalEngine, DEFAULT_THRESHOLDS } from './signalEngine';
import { createMidiManager } from './midi';
import { createSimulator } from './simulator';
import { outbox } from '@/show/outbox';
import { loadCalibration, saveCalibration, clearCalibration } from './calibration';
import { createBinaryTouch, autoTune, DETECTOR_DEFAULT, TOUCH_TEST_STEPS, sanitizeDetector } from './binaryTouch';

const SCOPE_WINDOW_MS = 10000;
const ROLES_KEY = 'spore.channelRoles.v1';
const STRICTNESS_KEY = 'spore.strictness.v1';
const BASELINE_KEY = 'spore.baseline.v1';
const TOUCH_CFG_KEY = 'spore.touchConfig.v5';
const DETECTOR_KEY = 'spore.detector.v5';
const now = () => performance.now();
const loadJson = (k, d) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? d;
  } catch {
    return d;
  }
};

function createRuntime() {
  const pipelineListeners = new Set();
  const gestureListeners = new Set();
  const calibration = { record: loadCalibration() };
  const strictness = { level: loadJson(STRICTNESS_KEY, 3) };
  const roles = loadJson(ROLES_KEY, {});
  const engine = createSignalEngine({ ...(calibration.record ? calibration.record.thresholds : {}), strictness: strictness.level }, { startT: now() });
  const counts = { PULSE: 0, SOLID: 0, STROKE: 0, BRUSH: 0, STILL: 0 };
  const lastGesture = { PULSE: null, SOLID: null, STROKE: null, BRUSH: null, STILL: null };
  const eventLog = [];
  const gestureLog = [];
  const channels = {};
  const scope = { cc: [], notes: [], gestures: [] };
  let seq = 0;
  let showProvider = () => null;

  function emit(ev) {
    seq += 1;
    const role = roles[ev.channel] || 'mushroom';
    const e = { ...ev, id: seq, role };
    eventLog.unshift(e);
    if (eventLog.length > 50) eventLog.pop();
    const ch = channels[e.channel] || (channels[e.channel] = { channel: e.channel, count: 0, lastT: 0, lastType: null });
    ch.count += 1;
    ch.lastT = e.t;
    ch.lastType = e.type;
    if (role === 'mushroom') {
      if (e.type === 'note_on') scope.notes.push({ t: e.t, v: e.velocity });
      engine.ingest(e);
    }
    pipelineListeners.forEach((fn) => fn(e));
  }

  engine.on((g) => {
    counts[g.type] += 1;
    lastGesture[g.type] = { ...g, n: counts[g.type] };
    if (g.type === 'PULSE' && g.solid) {
      counts.SOLID += 1;
      lastGesture.SOLID = { ...g, n: counts.SOLID };
    }
    gestureLog.unshift(g);
    if (gestureLog.length > 30) gestureLog.pop();
    scope.gestures.push(g);
    gestureListeners.forEach((fn) => fn(g));
  });

  const midi = createMidiManager(emit);
  const sim = createSimulator(emit);

  // Phase 5 binary touch detector (TOUCHING / NOT TOUCHING): drives the show.
  const touchCfg = loadJson(TOUCH_CFG_KEY, {});
  const touch = createBinaryTouch(touchCfg, { detector: sanitizeDetector(loadJson(DETECTOR_KEY, DETECTOR_DEFAULT)), startT: now() });
  const touchListeners = new Set();
  const touchCounts = { TOUCH_START: 0, TAP: 0, STROKE: 0, REBASELINE: 0 };
  const touchTest = { running: false, stepIndex: -1, prompt: null, stepOn: false, stepMs: 0, stepEndsAt: 0, result: loadJson('spore.touchTest.v5', null), error: null };
  const touchLog = [];
  const rawRing = [];
  const detections = [];
  const slim = (e) => ({ t: Math.round(e.t), src: e.source, type: e.type, channel: e.channel, note: e.note, velocity: e.velocity, cc: e.cc, value: e.value, role: e.role });
  const uploads = { last: null, count: 0, error: null };
  // via the local outbox: never blocks the show; the reply arrives when (if) the first send gets through
  async function uploadRecording(kind, { events, label = '', meta = {} } = {}) {
    const t0 = now() - 120000;
    const j = await outbox.post('/api/recordings', { kind, label, meta: { ...meta, source: sim.getState().enabled ? 'simulator' : midi.getInfo().status === 'connected' ? 'biotron' : 'none', device: midi.getInfo().deviceName, baseline: touch.getBaseline(), config: touch.getConfig(), userAgent: navigator.userAgent }, thresholds: touch.getState().thresholds, events: (events || rawRing).map(slim), detections: detections.filter((d) => d.t >= t0) });
    Object.assign(uploads, { last: { kind, ...j, at: new Date().toISOString() }, error: j.queued ? 'queued in outbox' : null });
    uploads.count += j.recording_id ? 1 : 0;
    return j;
  }
  touch.on((g) => {
    if (g.type === 'REBASELINE' && g.reason !== 'relearn') uploadRecording('auto_rebaseline', { events: rawRing.filter((e) => e.t >= g.t - 30000), meta: { reason: g.reason } });
    detections.push({ type: g.type, t: Math.round(g.t), durationMs: g.durationMs, symbol: g.symbol, reason: g.reason });
    while (detections.length && detections[0].t < g.t - 120000) detections.shift();
    if (g.type in touchCounts) touchCounts[g.type] += 1;
    if (g.type === 'SYMBOL') touchCounts[g.symbol] += 1;
    if (g.type === 'SYMBOL' || g.type === 'REBASELINE') {
      touchLog.unshift(g);
      if (touchLog.length > 30) touchLog.pop();
    }
    touchListeners.forEach((fn) => fn(g));
  });
  let testRec = null;
  let testRest = null;
  pipelineListeners.add((e) => {
    rawRing.push(e);
    while (rawRing.length && rawRing[0].t < e.t - 60000) rawRing.shift();
    if (e.role !== 'mushroom') return;
    if (testRec) testRec.push(e);
    touch.ingest(e);
  });
  let simWasOn = false;
  sim.subscribe((st) => {
    if (st.enabled && !simWasOn) touch.relearn(now());
    simWasOn = st.enabled;
  });

  // Guided touch test: labelled HANDS OFF / TOUCH windows -> auto-tuned detector (kept only if separation is decent).
  function startTouchTest({ onStep = () => {} } = {}) {
    if (touchTest.running) return Promise.resolve(null);
    testRec = [];
    const lastOf = (type) => [...rawRing].reverse().find((e) => e.role === 'mushroom' && e.type === type && (type !== 'cc' || e.cc === 90));
    const lc = lastOf('cc');
    const ln = lastOf('note_on');
    const br = touch.getBaseline().rest;
    testRest = lc || br ? { cc: lc ? lc.value : br.cc, note: ln ? ln.note : br ? br.note : 60, vel: ln ? ln.velocity : 60 } : null;
    const t0 = now();
    const windows = [];
    let at = t0;
    TOUCH_TEST_STEPS.forEach((s) => {
      windows.push({ on: s.on, hover: !!s.hover, start: at, end: at + s.ms, prompt: s.prompt });
      at += s.ms;
    });
    Object.assign(touchTest, { running: true, error: null });
    return new Promise((resolve) => {
      const run = (i) => {
        if (i >= windows.length) {
          const events = testRec;
          testRec = null;
          const r0 = testRest; // the Biotron is silent at rest: seed the test with the value it was holding when the test began
          const seed = r0 ? [{ t: windows[0].start, type: 'cc', cc: 90, value: r0.cc }, { t: windows[0].start, type: 'note_on', note: r0.note, velocity: r0.vel || 60 }] : [];
          const cc = [...seed, ...events].filter((e) => e.type === 'cc' && e.cc === 90).map((e) => ({ t: e.t, v: e.value }));
          const notes = [...seed, ...events].filter((e) => e.type === 'note_on' && e.velocity > 0).map((e) => ({ t: e.t, note: e.note, vel: e.velocity }));
          const res = autoTune({ cc, notes, windows });
          const previous = touch.getDetector();
          const hoverDet = res.hoverMode === 'tuned'
            ? { hoverMode: 'tuned', hoverOn: res.hoverOn, hoverOff: res.hoverOff, tapMin: res.tapMin }
            : { hoverMode: 'degraded', hoverOn: undefined, hoverOff: undefined, tapMin: undefined }; // poor hover: a tease accepts any touch
          const hoverSeps = { hoverSeparation: res.hoverSeparation, touchHoverSeparation: res.touchHoverSeparation };
          if (res.applied || res.hoverMode) {
            const det = res.applied
              ? { feature: res.feature, thrOn: res.thrOn, thrOff: res.thrOff, source: 'touch-test', separation: res.separation, ...hoverDet, ...hoverSeps }
              : { ...previous, hoverMode: 'degraded', ...hoverSeps }; // touch bands kept; never trust a hover band from a poor test
            touch.setDetector(det);
            localStorage.setItem(DETECTOR_KEY, JSON.stringify(det));
          }
          const result = { ...res, at: new Date().toISOString(), kept: res.applied ? null : previous.source, source: sim.getState().enabled ? 'simulator' : 'biotron' };
          localStorage.setItem('spore.touchTest.v5', JSON.stringify(result));
          Object.assign(touchTest, { running: false, stepIndex: -1, prompt: null, result });
          uploadRecording('touch_test', { events, label: `touch test · ${res.feature} · sep ${res.separation}`, meta: { windows: windows.map((w) => ({ ...w, start: Math.round(w.start), end: Math.round(w.end) })), result } }).then((r) => {
            touchTest.result = { ...touchTest.result, recordingId: r && r.recording_id };
          });
          resolve(touchTest.result);
          return;
        }
        const w = windows[i];
        Object.assign(touchTest, { stepIndex: i, prompt: w.prompt, stepOn: w.on, stepMs: w.end - w.start, stepEndsAt: w.end });
        onStep(i, w);
        setTimeout(() => run(i + 1), Math.max(0, w.end - now()));
      };
      run(0);
    });
  }

  function tick() {
    const t = now();
    const s = engine.tick(t);
    touch.tick(t);
    scope.cc.push({ t, v: s.cc });
    const cutoff = t - SCOPE_WINDOW_MS;
    ['cc', 'notes', 'gestures'].forEach((k) => {
      while (scope[k].length && scope[k][0].t < cutoff) scope[k].shift();
    });
  }
  setInterval(tick, 20);
  function saveTouchCfg() {
    const c = touch.getConfig();
    localStorage.setItem(TOUCH_CFG_KEY, JSON.stringify({ sensitivity: c.sensitivity }));
  }
  const touchTestState = () => ({ ...touchTest, remainingMs: touchTest.running ? Math.max(0, touchTest.stepEndsAt - now()) : 0 });

  const api = {
    engine,
    touch,
    touchCounts,
    touchLog,
    uploads,
    uploadRecording,
    saveSnapshot: (label = 'host X') => uploadRecording('snapshot_60s', { events: rawRing.slice(), label }),
    onTouch(fn) {
      touchListeners.add(fn);
      return () => touchListeners.delete(fn);
    },
    relearnBaseline() {
      touch.relearn(now());
    },
    startTouchTest,
    getTouchTest: touchTestState,
    resetDetector() {
      touch.setDetector(DETECTOR_DEFAULT);
      localStorage.removeItem(DETECTOR_KEY);
    },
    setSensitivity(level) {
      const v = Math.min(5, Math.max(1, Math.round(level)));
      touch.setConfig({ sensitivity: v });
      saveTouchCfg();
      return v;
    },
    getSensitivity: () => touch.getConfig().sensitivity,
    applyTouchConfig(partial) {
      touch.setConfig(partial);
      saveTouchCfg();
    },
    midi,
    sim,
    scope,
    now,
    emit,
    subscribe(fn) {
      pipelineListeners.add(fn);
      return () => pipelineListeners.delete(fn);
    },
    onGesture(fn) {
      gestureListeners.add(fn);
      return () => gestureListeners.delete(fn);
    },
    resetCounts() {
      Object.keys(counts).forEach((k) => {
        counts[k] = 0;
        lastGesture[k] = null;
      });
      gestureLog.length = 0;
    },
    applyCalibration(result) {
      calibration.record = saveCalibration(result);
      engine.setThresholds(result.thresholds);
      return calibration.record;
    },
    resetCalibration() {
      clearCalibration();
      calibration.record = null;
      engine.setThresholds({});
    },
    getCalibration: () => calibration.record,
    setChannelRole(channel, role) {
      if (role === 'mushroom') delete roles[channel];
      else roles[channel] = role;
      localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
    },
    getChannelRole: (channel) => roles[channel] || 'mushroom',
    setStrictness(level) {
      strictness.level = Math.min(5, Math.max(1, Math.round(level)));
      engine.setStrictness(strictness.level);
      localStorage.setItem(STRICTNESS_KEY, JSON.stringify(strictness.level));
      return strictness.level;
    },
    getStrictness: () => strictness.level,
    setShowProvider(fn) {
      showProvider = fn;
    },
    getSnapshot() {
      return {
        status: midi.getInfo(),
        sim: sim.getState(),
        state: engine.getState(),
        counts: { ...counts },
        lastGesture: { ...lastGesture },
        thresholds: engine.getThresholds(),
        strictness: strictness.level,
        calibrated: !!calibration.record,
        calibratedAt: calibration.record ? calibration.record.calibratedAt : null,
        events: eventLog.slice(),
        gestures: gestureLog.slice(),
        channels: Object.values(channels).map((c) => ({ ...c, role: roles[c.channel] || 'mushroom', ago: (now() - c.lastT) / 1000 })),
        roles: { ...roles },
        touch5: touch.getState(),
        touchTest: touchTestState(),
      };
    },
    defaults: DEFAULT_THRESHOLDS,
  };

  const debug = {};
  const detLog = [];
  touch.on((g) => {
    const st = touch.getState();
    detLog.push({ ...g, t: Math.round(g.t), cc: st.features.cc, rest: st.rest && Math.round(st.rest.cc), v: Math.round(st.value) });
    if (detLog.length > 80) detLog.shift();
  });
  debug.detLog = () => detLog.slice();
  debug.setWord = (word, enabled) => window.__sporeShow && window.__sporeShow.setWord(word, enabled, 'test'); // window.__spore.setWord('tap', false)
  debug.triggerCalm = () => window.__sporeShow && window.__sporeShow.triggerCalm('test'); // calm-down tendril game
  debug.soothe = (n = 1) => window.__sporeShow && Array.from({ length: n }, () => window.__sporeShow.soothe()).filter(Boolean).length;
  debug.calmYes = () => window.__sporeShow && window.__sporeShow.calmYes();
  debug.broochFound = () => window.__sporeShow && window.__sporeShow.broochFound(); // search screen: WE FOUND THE ROBIN BROOCH
  debug.glitch = (kind, on = true) => sim.setGlitch(kind, on); // window.__spore.glitch('phantomTap' | 'stuckTouch' | 'stuckHover')
  debug.subtitleLog = []; // last 10 subtitles (filled by the show controller)
  debug.recentRaw = (n = 80) => rawRing.filter((e) => e.type === 'cc').slice(-n).map((e) => [Math.round(e.t), e.value]);
  Object.defineProperty(debug, 'detections', { enumerable: true, get: () => detections.slice(-20) });
  ['status', 'state', 'counts', 'thresholds', 'sim', 'events', 'gestures', 'channels', 'roles', 'calibrated', 'lastGesture'].forEach((k) => {
    Object.defineProperty(debug, k, { enumerable: true, get: () => api.getSnapshot()[k] });
  });
  Object.defineProperty(debug, 'show', { enumerable: true, get: () => showProvider() });
  Object.defineProperty(debug, 'strictness', { enumerable: true, get: () => strictness.level });
  ['phase', 'chapterIndex', 'feeling', 'resonance', 'keyProgress', 'unlocked', 'preload', 'voiceQueue', 'muted', 'experiment', 'stepIndex', 'stepProgress', 'scriptId', 'voiceSources', 'excitation', 'longing', 'styleScores', 'feelingScores', 'dominantStyle', 'prompted', 'discovered', 'lastReveal', 'voiceId', 'guesses', 'level', 'levelFeeling', 'levelProgress', 'nudged', 'tryS', 'sequence', 'attempt', 'lenient', 'hostHint', 'videoReady', 'videoProgress', 'videoSource', 'videoError', 'videoPlay', 'videoNeedsClick', 'videoLocal', 'machinePhase', 'wordStatus', 'adaptedSequences', 'fallbackMode', 'storyAct', 'memoryFragments', 'calmGame'].forEach((k) => {
    Object.defineProperty(debug, k, { enumerable: true, get: () => (showProvider() || {})[k] });
  });
  Object.defineProperty(debug, 'touching', { enumerable: true, get: () => touch.getState().touching });
  Object.defineProperty(debug, 'hovering', { enumerable: true, get: () => touch.getState().hovering });
  Object.defineProperty(debug, 'contact', { enumerable: true, get: () => touch.getState().state3 });
  Object.defineProperty(debug, 'touchActive', { enumerable: true, get: () => touch.getState().touching });
  Object.defineProperty(debug, 'touchMs', { enumerable: true, get: () => touch.getState().touchMs });
  Object.defineProperty(debug, 'lastSymbol', { enumerable: true, get: () => touch.getState().lastSymbol });
  Object.defineProperty(debug, 'detector', { enumerable: true, get: () => touch.getState().detector });
  Object.defineProperty(debug, 'touchTest', { enumerable: true, get: () => touchTestState() });
  Object.defineProperty(debug, 'baseline', { enumerable: true, get: () => touch.getBaseline() });
  Object.defineProperty(debug, 'touch', { enumerable: true, get: () => ({ ...touch.getState(), counts: { ...touchCounts } }) });
  Object.defineProperty(debug, 'uploads', { enumerable: true, get: () => ({ ...uploads }) });
  Object.freeze(debug);
  Object.defineProperty(window, '__spore', { value: debug, writable: false, configurable: false });

  midi.autoConnectIfGranted();
  return api;
}

export const spore = window.__sporeRuntime || (window.__sporeRuntime = createRuntime());
